import React, {
  Component,
  ReactNode,
  Ref,
  KeyboardEvent,
  ChangeEvent
} from 'react'
import { css, cx } from '@emotion/css'
import Downshift, { StateChangeOptions, GetInputPropsOptions } from 'downshift'
import {
  CsDropdownCollapseIcon as CollapseIcon,
  CsDropdownExpandIcon as ExpandIcon
} from '../icons'
import {
  TextBox,
  textBoxBorderRule,
  TextBoxProps,
  textBoxReadOnlyStyle
} from '../form-text-box'
import { ComboboxItem, comboboxInputID, comboboxMenuItemID } from './shared'
import { ComboBoxMenu } from './combobox-menu'
import { Label } from '../form-label'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { MessageDescriptor } from 'react-intl'
import { FormTextContainer } from '../form-text'

type RenderInputValueFn<T> = (
  item?: '' | T | undefined,
  options?: {
    readonly disabled?: boolean
  }
) => React.ReactNode

export interface SingleSelectComboboxProps<T> {
  readonly disabled?: boolean
  readonly 'aria-labelledby'?: string
  readonly 'aria-label'?: string

  /**
   * Toggles an EDITED indicator on the field
   */
  readonly edited?: boolean

  /**
   * The id of the input element. Note: this should always be provided in tests
   */
  readonly id?: string

  readonly items: Array<ComboboxItem<T>>
  readonly itemsEqual?: (first: T, second: T) => boolean
  readonly itemToID?: (item: T) => string
  readonly renderInputValue?: RenderInputValueFn<T>
  readonly renderItemValue?: (item: T, inputValue: string) => React.ReactNode
  readonly itemIsDisabled?: (item: T) => boolean
  readonly renderFooter?: () => React.ReactNode
  readonly 'data-testid'?: string

  /**
   * Text describing the input element. This will be used as the content
   * for the label element unless the renderLabel prop is used
   */
  readonly label?: string

  readonly onChange?: (value: T) => void
  readonly onInputValueChange?: (value: string) => void

  /**
   * If provided this callback will render the contents of a help tooltip. Also,
   * a help icon will appear as part of the control's label that activates the
   * tooltip when hovered.
   */
  readonly renderHelpContent?: () => ReactNode

  /**
   * Can be used to render an alternate label or exclude the label element
   * altogether if the callback returns null/undefined. If this callback is
   * defined it will be passed the value of the label prop as the first argument
   * and the input element id as the second argument.
   */
  readonly renderLabel?: (string, id) => ReactNode

  /**
   * Optional placeholder for the select's empty state
   */
  readonly placeholder?: string

  readonly value?: T

  readonly isAddAllowed?: boolean
  readonly createMessage?: MessageDescriptor
  readonly onNewItemClick?: (inputValue: string) => void
  readonly itemFromInput?: (inputValue: string) => T

  readonly inputDataTestId?: string
  readonly comboboxDataTestId?: string
  readonly comboboxMenuItemToTestID?: (item: T) => string

  readonly inputProps?: TextBoxProps
  readonly maxHeight?: string
}

export interface SingleSelectComboboxConditionallyRequiredProps<T> {
  readonly itemToString?: (item: T) => string
}

interface State<T> {
  readonly inputValue: string
  readonly selectedItem: T | ''
}

interface DetermineValueToRenderProps<T> {
  readonly item: T | string
  readonly itemToString?: (item: string | T) => string
}

export const determineValueToRender: <T>(
  props: DetermineValueToRenderProps<T>
) => string = ({ item, itemToString }) => {
  if (item || (typeof item === 'number' && item === 0)) {
    if (typeof itemToString === 'function') {
      return itemToString(item)
    }

    if (typeof item === 'string' && itemToString === undefined) {
      return item
    }

    throw new Error('item must be a string if itemToString is not defined')
  }

  return ''
}

export const determineComboboxMenuItemToTestId: <T>(
  props: Pick<
    SingleSelectComboboxProps<T>,
    'itemToID' | 'comboboxMenuItemToTestID'
  >
) => SingleSelectComboboxProps<T>['comboboxMenuItemToTestID'] = ({
  comboboxMenuItemToTestID,
  itemToID
}) =>
  comboboxMenuItemToTestID
    ? comboboxMenuItemToTestID
    : (item) => {
        if (itemToID) {
          return `${itemToID(item)}-menu-item`
        }
        return comboboxMenuItemID
      }

export class SingleSelectCombobox<T> extends Component<
  SingleSelectComboboxProps<T> &
    SingleSelectComboboxConditionallyRequiredProps<T>,
  State<T>
> {
  public static defaultProps = {
    disabled: false
  }

  public static getDerivedStateFromProps(nextProps, currentState) {
    const newSelectedItem =
      typeof nextProps.value !== 'undefined' ? nextProps.value : ''
    if (!currentState || currentState.initialSelectedItem !== newSelectedItem) {
      return {
        ...currentState,
        selectedItem: newSelectedItem,
        initialSelectedItem: newSelectedItem
      }
    }
    return null
  }

  public constructor(
    props: SingleSelectComboboxProps<T> &
      SingleSelectComboboxConditionallyRequiredProps<T>
  ) {
    super(props)
    this.state = SingleSelectCombobox.getDerivedStateFromProps(props, {
      inputValue: ''
    })
  }

  public componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        selectedItem:
          typeof this.props.value !== 'undefined' ? this.props.value : ''
      })
    }
  }

  public render() {
    const {
      disabled,
      edited,
      id,
      items,
      itemToString,
      itemsEqual,
      itemToID,
      renderInputValue,
      renderItemValue,
      itemIsDisabled,
      label,
      renderHelpContent,
      renderLabel,
      placeholder = '-',
      isAddAllowed = false,
      createMessage,
      onNewItemClick,
      itemFromInput,
      inputDataTestId,
      comboboxDataTestId,
      comboboxMenuItemToTestID,
      renderFooter,
      inputProps,
      maxHeight,
      'aria-label': ariaLabel,
      'data-testid': dataTestID
    } = this.props

    const { inputValue, selectedItem } = this.state

    const labelNode = renderLabel
      ? renderLabel(label, id)
      : label && (
          <Label
            edited={edited}
            htmlFor={id}
            renderHelpContent={renderHelpContent}
          >
            {label}
          </Label>
        )

    return (
      <Downshift
        inputValue={inputValue}
        onChange={this.handleChange}
        selectedItem={selectedItem}
        itemToString={() => ''}
        onStateChange={(changes: StateChangeOptions<T>) => {
          if (
            changes.hasOwnProperty('selectedItem') &&
            typeof changes.selectedItem !== undefined
          ) {
            this.setState({
              selectedItem:
                changes.selectedItem !== null &&
                typeof changes.selectedItem !== 'undefined'
                  ? changes.selectedItem
                  : ''
            })
          }
        }}
      >
        {({
          getInputProps,
          getItemProps,
          isOpen,
          highlightedIndex,
          openMenu,
          closeMenu
        }) => {
          return (
            <div>
              <FormTextContainer {...getInputProps({ ...inputProps })}>
                {({ errorMessage, ...props }) => {
                  const {
                    onBlur: onBlurFromInput,
                    onFocus: onFocusFromInput,
                    renderLabel: _,
                    ...restInputProps
                  } = props

                  return (
                    <React.Fragment>
                      <div
                        className={css`
                          display: flex;
                          flex-direction: column;
                          width: 100%;
                          position: relative;
                        `}
                        data-testid={dataTestID}
                      >
                        {labelNode}
                        <ComboboxContainer
                          showExpandIndicator={true}
                          expanded={isOpen}
                          openMenu={openMenu}
                          closeMenu={closeMenu}
                          disabled={disabled}
                        >
                          {!isOpen && renderInputValue ? (
                            <CustomInput<T>
                              inputDataTestId={inputDataTestId}
                              disabled={disabled}
                              openMenu={openMenu}
                              renderInputValue={renderInputValue}
                              selectedItem={selectedItem}
                            />
                          ) : (
                            <DefaultInput<T>
                              {...restInputProps}
                              disabled={disabled}
                              getInputProps={getInputProps}
                              renderLabel={renderLabel}
                              id={id}
                              label={label}
                              renderInputValue={renderInputValue}
                              handleInputChange={this.handleInputChange}
                              handleKeyDown={this.handleKeyDown}
                              handleBlur={this.handleBlur}
                              isOpen={isOpen}
                              inputValue={inputValue}
                              selectedItem={selectedItem}
                              itemToString={itemToString}
                              placeholder={placeholder}
                              openMenu={openMenu}
                              aria-label={ariaLabel}
                              onFocusFromInput={onFocusFromInput}
                              onBlurFromInput={onBlurFromInput}
                              inputDataTestId={inputDataTestId}
                              errorMessage={errorMessage}
                            />
                          )}

                          {isOpen && (
                            <ComboBoxMenu<T>
                              comboboxDataTestId={comboboxDataTestId}
                              values={[]}
                              items={items}
                              itemToString={itemToString}
                              itemsEqual={itemsEqual}
                              itemToID={itemToID}
                              getItemProps={getItemProps}
                              highlightedIndex={highlightedIndex}
                              inputValue={inputValue}
                              isAddAllowed={isAddAllowed}
                              createMessage={createMessage}
                              renderItemValue={renderItemValue}
                              itemIsDisabled={itemIsDisabled}
                              onNewItemClick={onNewItemClick}
                              itemFromInput={itemFromInput}
                              renderFooter={renderFooter}
                              maxHeight={maxHeight}
                              comboboxMenuItemToTestID={determineComboboxMenuItemToTestId<T>(
                                { comboboxMenuItemToTestID, itemToID }
                              )}
                            />
                          )}
                        </ComboboxContainer>
                      </div>
                    </React.Fragment>
                  )
                }}
              </FormTextContainer>
            </div>
          )
        }}
      </Downshift>
    )
  }

  private handleChange = (value) => {
    if (this.props.onChange) {
      this.props.onChange(value)
    }
    this.setState({ inputValue: '' })
  }

  private handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ inputValue: e.target.value })
    if (this.props.onInputValueChange) {
      this.props.onInputValueChange(e.target.value)
    }
  }

  private handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 27) {
      e.stopPropagation()
      this.setState({ inputValue: '' })
    }
  }

  private handleBlur = () => {
    this.setState({ inputValue: '' })
  }
}

function CustomInput<T>({
  inputDataTestId,
  disabled,
  openMenu,
  renderInputValue,
  selectedItem
}: {
  readonly inputDataTestId?: string
  readonly disabled?: boolean
  readonly openMenu: (cb?: () => void | undefined) => void
  readonly renderInputValue: RenderInputValueFn<T>
  readonly selectedItem: '' | T
}) {
  return (
    <div
      data-testid={inputDataTestId || comboboxInputID}
      onClick={() => {
        if (!disabled) {
          openMenu()
        }
      }}
      className={cx(
        css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          border: 1px solid #bfbfbf;
          border-radius: 3px;
          background-color: ${theme.color.white};
          height: 34px;
          display: flex;
          align-items: center;
        `,
        disabled ? textBoxReadOnlyStyle : undefined
      )}
    >
      {renderInputValue(selectedItem, {
        disabled
      })}
    </div>
  )
}

function DefaultInput<T>({
  disabled = false,
  getInputProps,
  renderLabel: _renderLabel,
  id,
  label,
  renderInputValue,
  handleInputChange,
  isOpen,
  inputValue,
  placeholder,
  openMenu,
  onFocusFromInput,
  onBlurFromInput,
  inputDataTestId,
  errorMessage,
  handleKeyDown,
  handleBlur,
  selectedItem,
  itemToString,
  'aria-label': ariaLabel,
  ...restInputProps
}: {
  readonly disabled?: boolean
  readonly id?: string
  readonly label?: string
  readonly placeholder: string
  readonly isOpen: boolean
  readonly inputValue: string
  readonly inputDataTestId?: string
  readonly 'aria-label'?: string
  readonly errorMessage?: string
  readonly getInputProps: (options?: GetInputPropsOptions | undefined) => any
  readonly renderLabel?: (string: any, id: any) => React.ReactNode
  readonly renderInputValue?: RenderInputValueFn<T>
  readonly handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  readonly handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  readonly handleBlur: () => void
  readonly openMenu: (cb?: () => void | undefined) => void
  readonly onFocusFromInput?: (
    event?: React.FocusEvent<HTMLInputElement>
  ) => void
  readonly onBlurFromInput?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  readonly selectedItem: '' | T
  readonly itemToString?: (item: T) => string
}) {
  const className = isOpen
    ? css`
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom: ${textBoxBorderRule};

        &:focus {
          border-bottom: ${textBoxBorderRule};
        }
      `
    : undefined

  const calculatedAriaLabel = label ? label : ariaLabel

  return (
    <TextBox
      {...getInputProps({
        ...restInputProps,
        disabled,
        // should not be set in favor of the label and aria-label
        'aria-labelledby': undefined,
        // if I have a label and id, I don't need this, otherwise use what I have
        id,
        'aria-label': label && id ? undefined : calculatedAriaLabel,
        autoFocus: !!renderInputValue,
        onChange: handleInputChange,
        className: cx(
          className,
          disabled
            ? cx(
                textBoxReadOnlyStyle,
                css`
                  &[disabled] {
                    cursor: not-allowed;
                  }
                `
              )
            : undefined
        ),
        value: isOpen
          ? inputValue
          : determineValueToRender({
              item: selectedItem,
              itemToString
            }),
        placeholder,
        onKeyDown: handleKeyDown,
        onFocus: () => {
          openMenu()
          if (!!onFocusFromInput) {
            onFocusFromInput()
          }
        },
        onBlur: (e: React.FocusEvent<any>) => {
          handleBlur()
          if (!!onBlurFromInput) {
            onBlurFromInput(e)
          }
        }
      })}
      data-testid={inputDataTestId || comboboxInputID}
      data-e2e={`combobox-${label && id ? undefined : calculatedAriaLabel}`}
      error={!!errorMessage}
    />
  )
}

const comboboxExpandStyle = css`
  position: absolute;
  right: 10px;
  top: 11px;
`

export function ComboboxContainer({
  children,
  expanded,
  innerRef,
  showExpandIndicator,
  openMenu,
  closeMenu,
  disabled
}: {
  readonly children: ReactNode
  readonly expanded?: boolean
  readonly innerRef?: Ref<HTMLDivElement>
  readonly showExpandIndicator?: boolean
  readonly openMenu: () => void
  readonly closeMenu: () => void
  readonly disabled: boolean | undefined
}) {
  const iconClassName = cx(
    comboboxExpandStyle,
    css`
      color: ${disabled ? theme.color.neutral60 : theme.color.neutral100};
    `
  )
  return (
    <div
      ref={innerRef}
      className={css`
        position: relative;
      `}
    >
      {children}
      {showExpandIndicator &&
        (expanded ? (
          <CollapseIcon
            onClick={() => {
              if (!disabled) {
                closeMenu()
              }
            }}
            className={iconClassName}
          />
        ) : (
          <ExpandIcon
            onClick={() => {
              if (!disabled) {
                openMenu()
              }
            }}
            className={iconClassName}
          />
        ))}
    </div>
  )
}
